import { assert } from '@ember/debug'
import { cached } from '@glimmer/tracking'
import { groupBy, isMatch } from 'lodash'
import { Precinct } from 'teacher-ui-client/utils/precincts'
import type GradeSetsService from 'teacher-ui-client/services/grade-sets'
import type { IntlService } from 'ember-intl'
import type LessonInfoService from 'teacher-ui-client/services/lesson-info'
import type ProgressionSequenceService from 'teacher-ui-client/services/progression-sequence'
import type { MathseedsPrimeThread } from 'teacher-ui-client/progression-sequence/utils'

/**
 * Welcome to the progress utils file
 *
 * Here you'll find all the logic related to progress that allows us to;
 * - create options from lesson info
 * - read progression data to match it to an option (for example, to show users' saved progress)
 * - serialize option for saving
 *
 * Every precinct does something different. That's why this file is large. Differences include;
 * - inconsistent lesson info (sometimes numbers, sometimes strings, varying properties).
 * - progression records (both in properties available & the product/precinct strings)
 * - how the selected option is serialized
 * - whether a placement test is an available option
 * - whether the options are grouped by grades or not
 *
 * The classes below help to manage all of that, providing an interface to get the available options, selected option
 * so that the components can remain dumb and simple.
 *
 * There's Option classes and ProgressHandler's for each precinct
 *
 * lessonOptions are cached, since they shouldn't change. Just make sure the required lesson info is loaded prior to
 * retrieving it.
 */

export abstract class BaseOption {
  intl: IntlService

  /**
   * Used to differentiate between progress options. Options in a single dropdown will have the same progress type.
   */
  progressType: ProgressHandler['progressType']

  /**
   * a unique id for the option - used to differentiate this option from others in select/option elements. Only needs
   * to be unique within the set of options for the given handler/precinct
   */
  abstract get optionId(): string

  /**
   * Label that will be used in the UI when rendering this option for selection
   */
  abstract get label(): string

  /**
   * Label that will be used in the UI (typically table cells) to indicate the position of this option
   */
  abstract get positionLabel(): string

  /**
   * Provides a progress json that can be sent to progression if this option is selected
   */
  abstract get serializedOption(): Record<PropertyKey, unknown>

  /**
   * Extra attributes to add in to the serialized option. Usually a product/precinct
   */
  abstract serializeAttrs?: Record<PropertyKey, unknown>

  constructor(intl: IntlService, progressType: ProgressHandler['progressType']) {
    this.intl = intl
    this.progressType = progressType
  }
}

class PlacementOption extends BaseOption {
  serializeAttrs?: Record<PropertyKey, unknown>

  get optionId() {
    return `${this.progressType}-placement`
  }

  get label() {
    return this.intl.t('placementTest')
  }

  get positionLabel() {
    return this.label
  }

  get serializedOption() {
    return {
      ...(this.serializeAttrs ?? {}),
      current_position: '0',
      show_placement_test: true,
    }
  }

  constructor(
    intl: IntlService,
    progressType: ProgressHandler['progressType'],
    serializeAttrs?: Record<PropertyKey, unknown>,
  ) {
    super(intl, progressType)
    this.serializeAttrs = serializeAttrs
  }
}

class ReadingEggsReadingOption extends BaseOption {
  serializeAttrs?: Record<PropertyKey, unknown>

  /**
   * stringint (eg '1')
   */
  lessonPosition: string

  lessonName: string

  /**
   * Grade position to group the option by
   */
  gradePosition: number

  get optionId() {
    return `${this.progressType}-${this.lessonPosition}`
  }

  get label() {
    return `${this.intl.t('lesson')} ${this.lessonPosition} - ${this.lessonName}`
  }

  get positionLabel() {
    return this.lessonPosition
  }

  get serializedOption() {
    return {
      ...(this.serializeAttrs ?? {}),
      current_position: this.lessonPosition,
      show_placement_test: false,
    }
  }

  constructor(
    intl: IntlService,
    progressType: ProgressHandler['progressType'],
    { lessonNumber, name, gradePosition }: { lessonNumber: string; name: string; gradePosition: number },
    serializeAttrs?: Record<PropertyKey, unknown>,
  ) {
    super(intl, progressType)
    this.lessonPosition = lessonNumber
    this.lessonName = name
    this.gradePosition = gradePosition
    this.serializeAttrs = serializeAttrs
  }
}

class ReadingEggsSpellingOption extends BaseOption {
  serializeAttrs?: Record<PropertyKey, unknown>

  /**
   * stringint (eg '1')
   */
  lessonPosition: string

  lessonName: string

  /**
   * Grade position to group the option by
   */
  gradePosition: number

  get optionId() {
    return `${this.progressType}-${this.lessonPosition}`
  }

  get label() {
    return `${this.intl.t('lesson')} ${this.lessonPosition} - ${this.lessonName}`
  }

  get positionLabel() {
    return this.lessonPosition
  }

  get serializedOption() {
    return {
      ...(this.serializeAttrs ?? {}),
      current_position: this.lessonPosition,
      show_placement_test: false,
    }
  }

  constructor(
    intl: IntlService,
    progressType: ProgressHandler['progressType'],
    { lessonNumber, name, gradePosition }: { lessonNumber: string; name: string; gradePosition: number },
    serializeAttrs?: Record<PropertyKey, unknown>,
  ) {
    super(intl, progressType)
    this.lessonPosition = lessonNumber
    this.lessonName = name
    this.gradePosition = gradePosition
    this.serializeAttrs = serializeAttrs
  }
}

class ReadingEggsStorylandsOption extends BaseOption {
  serializeAttrs?: Record<PropertyKey, unknown>

  /**
   * stringint (eg '1')
   */
  lessonPosition: string

  lessonName: string

  /**
   * Grade position to group the option by
   */
  gradePosition: number

  get optionId() {
    return `${this.progressType}-${this.lessonPosition}`
  }

  get label() {
    return `${this.intl.t('lesson')} ${this.lessonPosition} - ${this.lessonName}`
  }

  get positionLabel() {
    return this.lessonPosition
  }

  get serializedOption() {
    return {
      ...(this.serializeAttrs ?? {}),
      current_position: this.lessonPosition,
      show_placement_test: false,
    }
  }

  constructor(
    intl: IntlService,
    progressType: ProgressHandler['progressType'],
    { lessonNumber, name, gradePosition }: { lessonNumber: string; name: string; gradePosition: number },
    serializeAttrs?: Record<PropertyKey, unknown>,
  ) {
    super(intl, progressType)
    this.lessonPosition = lessonNumber
    this.lessonName = name
    this.gradePosition = gradePosition
    this.serializeAttrs = serializeAttrs
  }
}

class ReadingEggspressReadingOption extends BaseOption {
  serializeAttrs?: Record<PropertyKey, unknown>

  /**
   * stringint (eg '1')
   */
  lessonPosition: string

  lessonName: string

  /**
   * Grade position to group the option by
   */
  gradePosition: number

  get optionId() {
    return `${this.progressType}-${this.lessonPosition}`
  }

  get label() {
    return `${this.intl.t('lesson')} ${this.lessonPosition} - ${this.lessonName}`
  }

  get positionLabel() {
    return this.lessonPosition
  }

  get serializedOption() {
    return {
      ...(this.serializeAttrs ?? {}),
      current_position: this.lessonPosition,
      show_placement_test: false,
    }
  }

  constructor(
    intl: IntlService,
    progressType: ProgressHandler['progressType'],
    { lessonNumber, name, gradePosition }: { lessonNumber: string; name: string; gradePosition: number },
    serializeAttrs?: Record<PropertyKey, unknown>,
  ) {
    super(intl, progressType)
    this.lessonPosition = lessonNumber
    this.lessonName = name
    this.gradePosition = gradePosition
    this.serializeAttrs = serializeAttrs
  }
}

class ReadingEggspressSpellingOption extends BaseOption {
  serializeAttrs?: Record<PropertyKey, unknown>

  /**
   * stringint (eg '1')
   */
  lessonPosition: string

  lessonName: string

  /**
   * Grade position to group the option by
   */
  gradePosition: number

  get optionId() {
    return `${this.progressType}-${this.lessonPosition}`
  }

  get label() {
    return `${this.intl.t('lesson')} ${this.lessonPosition} - ${this.lessonName}`
  }

  get positionLabel() {
    return this.lessonPosition
  }

  get serializedOption() {
    return {
      ...(this.serializeAttrs ?? {}),
      current_position: this.lessonPosition,
      show_placement_test: false,
    }
  }

  constructor(
    intl: IntlService,
    progressType: ProgressHandler['progressType'],
    { lessonNumber, name, gradePosition }: { lessonNumber: string; name: string; gradePosition: number },
    serializeAttrs?: Record<PropertyKey, unknown>,
  ) {
    super(intl, progressType)
    this.lessonPosition = lessonNumber
    this.lessonName = name
    this.gradePosition = gradePosition
    this.serializeAttrs = serializeAttrs
  }
}

class FastPhonicsPeaksOption extends BaseOption {
  serializeAttrs?: Record<PropertyKey, unknown>

  /**
   * stringint (eg '1')
   */
  lessonPosition: string

  lessonName: string

  get optionId() {
    return `${this.progressType}-${this.lessonPosition}`
  }

  get label() {
    return this.lessonName
  }

  get positionLabel() {
    return this.lessonPosition
  }

  get serializedOption() {
    return {
      ...(this.serializeAttrs ?? {}),
      current_position: this.lessonPosition,
      show_placement_test: false,
    }
  }

  constructor(
    intl: IntlService,
    progressType: ProgressHandler['progressType'],
    { lessonNumber, name }: { lessonNumber: string; name: string },
    serializeAttrs?: Record<PropertyKey, unknown>,
  ) {
    super(intl, progressType)
    this.lessonPosition = lessonNumber
    this.lessonName = name
    this.serializeAttrs = serializeAttrs
  }
}

class WritingLegendsLessonsOption extends BaseOption {
  serializeAttrs?: Record<PropertyKey, unknown>

  /**
   * stringint (eg '1')
   */
  mapPosition: string
  /**
   * stringint (eg '1')
   */
  lessonPosition: string

  get optionId() {
    return `${this.progressType}-${this.lessonPosition}`
  }

  get label() {
    return `${this.intl.t('map')} ${this.mapPosition} ${this.intl.t('lesson')} ${this.lessonPosition}`
  }

  get positionLabel() {
    return this.lessonPosition
  }

  get serializedOption() {
    return {
      ...(this.serializeAttrs ?? {}),
      current_position: this.lessonPosition,
      show_placement_test: false,
    }
  }

  constructor(
    intl: IntlService,
    progressType: ProgressHandler['progressType'],
    { mapNumber, lessonNumber }: { mapNumber: string; lessonNumber: string },
    serializeAttrs?: Record<PropertyKey, unknown>,
  ) {
    super(intl, progressType)
    this.mapPosition = mapNumber
    this.lessonPosition = lessonNumber
    this.serializeAttrs = serializeAttrs
  }
}

class MathseedsLessonsOption extends BaseOption {
  serializeAttrs?: Record<PropertyKey, unknown>

  /**
   * stringint (eg '1')
   */
  lessonPosition: string

  lessonName: string

  /**
   * Grade position to group the option by
   */
  gradePosition: number

  get optionId() {
    return `${this.progressType}-${this.lessonPosition}`
  }

  get label() {
    return `${this.intl.t('lesson')} ${this.lessonPosition} - ${this.lessonName}`
  }

  get positionLabel() {
    return this.lessonPosition
  }

  get serializedOption() {
    return {
      ...(this.serializeAttrs ?? {}),
      current_position: this.lessonPosition,
      show_placement_test: false,
    }
  }

  constructor(
    intl: IntlService,
    progressType: ProgressHandler['progressType'],
    { lessonNumber, name, gradePosition }: { lessonNumber: string; name: string; gradePosition: number },
    serializeAttrs?: Record<PropertyKey, unknown>,
  ) {
    super(intl, progressType)
    this.lessonPosition = lessonNumber
    this.lessonName = name
    this.gradePosition = gradePosition
    this.serializeAttrs = serializeAttrs
  }
}

export enum MathseedsMentalMinuteCategory {
  Addition = 'addition',
  Subtraction = 'subtraction',
  Multiplication = 'multiplication',
  Division = 'division',
}

function isMathseedsMentalMinuteCategory(maybeCategory: unknown): maybeCategory is MathseedsMentalMinuteCategory {
  return (
    typeof maybeCategory === 'string' &&
    (Object.values(MathseedsMentalMinuteCategory) as string[]).includes(maybeCategory)
  )
}

interface MathseedsMentalMinuteOptionData {
  lessonNumber: string
  name: string
  category: MathseedsMentalMinuteCategory
  gradePosition: number
}

class MathseedsMentalMinuteOption extends BaseOption {
  serializeAttrs?: Record<PropertyKey, unknown>

  /**
   * stringint (eg '1')
   */
  lessonPosition: string

  lessonName: string

  category: MathseedsMentalMinuteCategory

  /**
   * Grade position to group the option by
   */
  gradePosition: number

  get optionId() {
    return `${this.progressType}-${this.lessonPosition}`
  }

  get label() {
    return `${this.intl.t('lesson')} ${this.lessonPosition} - ${this.lessonName}`
  }

  get positionLabel() {
    return this.lessonPosition
  }

  get serializedOption() {
    return {
      ...(this.serializeAttrs ?? {}),
      current_position: this.lessonPosition,
      show_placement_test: false,
      category: this.category,
    }
  }

  constructor(
    intl: IntlService,
    progressType: ProgressHandler['progressType'],
    { lessonNumber, name, category, gradePosition }: MathseedsMentalMinuteOptionData,
    serializeAttrs?: Record<PropertyKey, unknown>,
  ) {
    super(intl, progressType)
    this.lessonPosition = lessonNumber
    this.lessonName = name
    this.category = category
    this.gradePosition = gradePosition
    this.serializeAttrs = serializeAttrs
  }
}

interface MathseedsPrimeLessonsOptionData {
  threadId: MathseedsPrimeThread
  unitId: number
  lessonId: number
  lessonName: string
  gradeName: string
}

class MathseedsPrimeLessonsOption extends BaseOption {
  serializeAttrs?: Record<PropertyKey, unknown>

  threadId: MathseedsPrimeThread

  unitId: number

  lessonId: number

  lessonName: string

  /**
   * Grade name to group the option by
   */
  gradeName: string

  get optionId() {
    return `${this.progressType}-${this.unitId}.${this.lessonId}`
  }

  get label() {
    const threadCode = this.intl.t(`mathseedsPrimeThreads.${this.threadId}.code`)
    return `${threadCode}.${this.unitId}.${this.lessonId} - ${this.lessonName}`
  }

  get positionLabel() {
    const threadCode = this.intl.t(`mathseedsPrimeThreads.${this.threadId}.code`)
    return `${threadCode}.${this.unitId}.${this.lessonId}`
  }

  get serializedOption() {
    return {
      ...(this.serializeAttrs ?? {}),
      thread: this.threadId,
      unit: this.unitId,
      lesson: this.lessonId,
    }
  }

  constructor(
    intl: IntlService,
    progressType: ProgressHandler['progressType'],
    { threadId, unitId, lessonId, lessonName, gradeName }: MathseedsPrimeLessonsOptionData,
    serializeAttrs?: Record<PropertyKey, unknown>,
  ) {
    super(intl, progressType)
    this.threadId = threadId
    this.unitId = unitId
    this.lessonId = lessonId
    this.lessonName = lessonName
    this.gradeName = gradeName
    this.serializeAttrs = serializeAttrs
  }
}

/**
 * A rough representation of progression records received from the /multiple_progresses progression api endpoint
 */
export interface ProgressionRecord {
  product: string
  precinct: string
  showPlacementTest?: boolean
  currentPosition?: Nullable<string | number>
  pendingPosition?: Nullable<string | number>
  [name: PropertyKey]: unknown
  /**
   * specific to fast phonics
   */
  lesson: number
  /**
   * specific to maths mental minute
   */
  results?: {
    lesson_set?: {
      addition_subtraction?: number
      multiplication_division?: number
    }
  }
}

/**
 * A progress handler for a given set of progress options (usually per precinct). It provides options, functions for
 * finding options and a function to normalize an option into a payload intended for saving progress.
 */
export interface ProgressHandler {
  /**
   * The progress type. Usually precinct, but not always. It should be unique to a progress selection
   */
  progressType: string
  /**
   * The common attributes used by this progress handler to;
   * - request related progression records
   * - identify those progression records in a response
   * - save/serialize new progression records
   */
  payloadAttributes: { product: string; precinct: string }
  /**
   * The option that indicates a placement test. Considered separately to lesson options, and is not always required.
   */
  placementOption?: BaseOption
  /**
   * All lesson options, as an array
   */
  lessonOptions: BaseOption[]
  /**
   * Options to be presented ungrouped in the selection - typically a placement option.
   */
  flatOptions?: BaseOption[]
  /**
   * Options to be presented grouped in the selection - typically grouped by grade. The keys are the group labels.
   */
  groupedOptions?: Record<string, BaseOption[]>
  /**
   * This function accepts an array of progression records and locates a matching option from what is available.
   *
   * This is useful for finding the matching option from an array of progression records for a single student.
   */
  getSavedOption: (studentProgressionRecords: ProgressionRecord[]) => BaseOption | undefined
}

export class ReadingEggsReadingProgressHandler implements ProgressHandler {
  intl: IntlService

  lessonInfo: LessonInfoService

  gradeSets: GradeSetsService

  progressType = Precinct.RE_READING

  payloadAttributes

  static payloadAttributes = { product: 're', precinct: 'lessons' }

  /**
   * Pass in the required services - can't inject ember services directly into native classes.
   */
  constructor(intl: IntlService, lessonInfo: LessonInfoService, gradeSets: GradeSetsService) {
    this.intl = intl
    this.lessonInfo = lessonInfo
    this.gradeSets = gradeSets

    this.payloadAttributes = ReadingEggsReadingProgressHandler.payloadAttributes
  }

  get placementOption(): BaseOption {
    return new PlacementOption(this.intl, this.progressType, this.payloadAttributes)
  }

  @cached
  get lessonOptions() {
    return this.lessonInfo.lessonsFor(Precinct.RE_READING).map<ReadingEggsReadingOption>((lesson) => {
      const { lesson_number: lessonNumber, name, grade_position: gradePosition } = lesson

      assert(
        'unexpected lesson data when building progress options',
        typeof gradePosition === 'number' && typeof lessonNumber === 'string' && name,
      )

      return new ReadingEggsReadingOption(
        this.intl,
        this.progressType,
        { lessonNumber, name, gradePosition },
        this.payloadAttributes,
      )
    })
  }

  get flatOptions(): BaseOption[] {
    return [this.placementOption]
  }

  get groupedOptions(): Record<string, ReadingEggsReadingOption[]> {
    return groupBy(this.lessonOptions, (opt) => this.gradeSets.fullNameFromGradeSetByPosition(opt.gradePosition))
  }

  getSavedOption(studentProgression: ProgressionRecord[]): ReadingEggsReadingOption | BaseOption | undefined {
    const progressItem = studentProgression.find((p) => isMatch(p, this.payloadAttributes))

    if (progressItem?.showPlacementTest || progressItem?.currentPosition === 'placement') {
      return this.placementOption
    } else if (progressItem?.currentPosition) {
      return this.lessonOptions.find((opt) => opt.lessonPosition === progressItem.currentPosition)
    }
  }
}

export class ReadingEggsSpellingProgressHandler implements ProgressHandler {
  intl: IntlService

  lessonInfo: LessonInfoService

  gradeSets: GradeSetsService

  progressType = Precinct.RE_SPELLING

  payloadAttributes

  static payloadAttributes = { product: 're', precinct: 'spelling' }

  constructor(intl: IntlService, lessonInfo: LessonInfoService, gradeSets: GradeSetsService) {
    this.intl = intl
    this.lessonInfo = lessonInfo
    this.gradeSets = gradeSets

    this.payloadAttributes = ReadingEggsSpellingProgressHandler.payloadAttributes
  }

  get placementOption(): BaseOption {
    return new PlacementOption(this.intl, this.progressType, this.payloadAttributes)
  }

  @cached
  get lessonOptions() {
    return this.lessonInfo.lessonsFor(Precinct.RE_SPELLING).map<ReadingEggsSpellingOption>((lesson) => {
      const { lesson_number: lessonNumber, name, grade_position: gradePosition } = lesson
      assert(
        'unexpected lesson data when building progress options',
        typeof gradePosition === 'number' && typeof lessonNumber === 'string' && name,
      )

      return new ReadingEggsSpellingOption(
        this.intl,
        this.progressType,
        { lessonNumber, name, gradePosition },
        this.payloadAttributes,
      )
    })
  }

  get flatOptions(): BaseOption[] {
    return [this.placementOption]
  }

  get groupedOptions(): Record<string, ReadingEggsSpellingOption[]> {
    return groupBy(this.lessonOptions, (opt) => this.gradeSets.fullNameFromGradeSetByPosition(opt.gradePosition))
  }

  getSavedOption(studentProgression: ProgressionRecord[]): ReadingEggsSpellingOption | BaseOption | undefined {
    const progressItem = studentProgression.find((p) => isMatch(p, this.payloadAttributes))

    if (progressItem?.showPlacementTest || progressItem?.currentPosition === 'placement') {
      return this.placementOption
    } else if (progressItem?.currentPosition) {
      return this.lessonOptions.find((opt) => opt.lessonPosition === progressItem.currentPosition)
    }
  }
}

export class ReadingEggsStorylandsProgressHandler implements ProgressHandler {
  intl: IntlService

  lessonInfo: LessonInfoService

  gradeSets: GradeSetsService

  progressType = Precinct.RE_STORYLANDS

  payloadAttributes

  static payloadAttributes = { product: 're', precinct: 'clinker_castle' }

  constructor(intl: IntlService, lessonInfo: LessonInfoService, gradeSets: GradeSetsService) {
    this.intl = intl
    this.lessonInfo = lessonInfo
    this.gradeSets = gradeSets

    this.payloadAttributes = ReadingEggsStorylandsProgressHandler.payloadAttributes
  }

  @cached
  get lessonOptions() {
    return this.lessonInfo.lessonsFor(Precinct.RE_STORYLANDS).map<ReadingEggsStorylandsOption>((lesson) => {
      const { lesson_number: lessonNumber, name, grade_position: gradePosition } = lesson
      assert(
        'unexpected lesson data when building progress options',
        typeof gradePosition === 'number' && typeof lessonNumber === 'string' && name,
      )

      return new ReadingEggsStorylandsOption(
        this.intl,
        this.progressType,
        { lessonNumber, name, gradePosition },
        this.payloadAttributes,
      )
    })
  }

  get groupedOptions(): Record<string, ReadingEggsStorylandsOption[]> {
    return groupBy(this.lessonOptions, (opt) => this.gradeSets.fullNameFromGradeSetByPosition(opt.gradePosition))
  }

  getSavedOption(studentProgression: ProgressionRecord[]): ReadingEggsStorylandsOption | BaseOption | undefined {
    const progressItem = studentProgression.find((p) => isMatch(p, this.payloadAttributes))

    if (progressItem?.currentPosition) {
      return this.lessonOptions.find((opt) => opt.lessonPosition === String(progressItem.currentPosition))
    }
  }
}

export class ReadingEggspressReadingProgressHandler implements ProgressHandler {
  intl: IntlService

  lessonInfo: LessonInfoService

  gradeSets: GradeSetsService

  progressType = Precinct.REX_READING

  payloadAttributes

  static payloadAttributes = { product: 'rex', precinct: 'my_lessons' }

  /**
   * Pass in the required services - can't inject ember services directly into native classes.
   */
  constructor(intl: IntlService, lessonInfo: LessonInfoService, gradeSets: GradeSetsService) {
    this.intl = intl
    this.lessonInfo = lessonInfo
    this.gradeSets = gradeSets

    this.payloadAttributes = ReadingEggspressReadingProgressHandler.payloadAttributes
  }

  get placementOption(): BaseOption {
    return new PlacementOption(this.intl, this.progressType, this.payloadAttributes)
  }

  @cached
  get lessonOptions() {
    return this.lessonInfo.lessonsFor(Precinct.REX_READING).map<ReadingEggspressReadingOption>((lesson) => {
      const { lesson_number: lessonNumber, name, grade_position: gradePosition } = lesson

      assert(
        'unexpected lesson data when building progress options',
        typeof gradePosition === 'number' && typeof lessonNumber === 'string' && name,
      )

      return new ReadingEggspressReadingOption(
        this.intl,
        this.progressType,
        { lessonNumber, name, gradePosition },
        this.payloadAttributes,
      )
    })
  }

  get flatOptions(): BaseOption[] {
    return [this.placementOption]
  }

  get groupedOptions(): Record<string, ReadingEggspressReadingOption[]> {
    return groupBy(this.lessonOptions, (opt) => this.gradeSets.fullNameFromGradeSetByPosition(opt.gradePosition))
  }

  getSavedOption(studentProgression: ProgressionRecord[]): ReadingEggspressReadingOption | BaseOption | undefined {
    const progressItem = studentProgression.find((p) => isMatch(p, this.payloadAttributes))

    if (progressItem?.showPlacementTest || progressItem?.currentPosition === 'placement') {
      return this.placementOption
    } else if (progressItem?.currentPosition) {
      return this.lessonOptions.find((opt) => opt.lessonPosition === progressItem.currentPosition)
    }
  }
}

export class ReadingEggspressSpellingProgressHandler implements ProgressHandler {
  intl: IntlService

  lessonInfo: LessonInfoService

  gradeSets: GradeSetsService

  progressType = Precinct.REX_SPELLING

  payloadAttributes

  static payloadAttributes = { product: 'rex', precinct: 'english_skills_spelling' }

  /**
   * Pass in the required services - can't inject ember services directly into native classes.
   */
  constructor(intl: IntlService, lessonInfo: LessonInfoService, gradeSets: GradeSetsService) {
    this.intl = intl
    this.lessonInfo = lessonInfo
    this.gradeSets = gradeSets

    this.payloadAttributes = ReadingEggspressSpellingProgressHandler.payloadAttributes
  }

  @cached
  get lessonOptions() {
    return this.lessonInfo.lessonsFor(Precinct.REX_SPELLING).map<ReadingEggspressSpellingOption>((lesson) => {
      const { lesson_number: lessonNumber, name, grade_position: gradePosition } = lesson

      assert(
        'unexpected lesson data when building progress options',
        typeof gradePosition === 'number' && typeof lessonNumber === 'string' && name,
      )

      return new ReadingEggspressSpellingOption(
        this.intl,
        this.progressType,
        { lessonNumber, name, gradePosition },
        this.payloadAttributes,
      )
    })
  }

  get groupedOptions(): Record<string, ReadingEggspressSpellingOption[]> {
    return groupBy(this.lessonOptions, (opt) => this.gradeSets.fullNameFromGradeSetByPosition(opt.gradePosition))
  }

  getSavedOption(studentProgression: ProgressionRecord[]): ReadingEggspressSpellingOption | BaseOption | undefined {
    const progressItem = studentProgression.find((p) => isMatch(p, this.payloadAttributes))

    if (progressItem?.currentPosition) {
      return this.lessonOptions.find((opt) => opt.lessonPosition === progressItem.currentPosition)
    }
  }
}

export class FastPhonicsPeaksProgressHandler implements ProgressHandler {
  intl: IntlService

  lessonInfo: LessonInfoService

  progressType = Precinct.FP_PEAKS

  payloadAttributes

  static payloadAttributes = { product: 'fast_phonics', precinct: 'lessons' }

  /**
   * Pass in the required services - can't inject ember services directly into native classes.
   */
  constructor(intl: IntlService, lessonInfo: LessonInfoService) {
    this.intl = intl
    this.lessonInfo = lessonInfo

    this.payloadAttributes = FastPhonicsPeaksProgressHandler.payloadAttributes
  }

  get placementOption(): BaseOption {
    return new PlacementOption(this.intl, this.progressType, this.payloadAttributes)
  }

  @cached
  get lessonOptions() {
    return this.lessonInfo.lessonsFor(Precinct.FP_PEAKS).map<FastPhonicsPeaksOption>((lesson) => {
      const { lesson_number: lessonNumber, name } = lesson

      assert('unexpected lesson data when building progress options', typeof lessonNumber === 'number' && name)

      return new FastPhonicsPeaksOption(
        this.intl,
        this.progressType,
        { lessonNumber: String(lessonNumber), name },
        this.payloadAttributes,
      )
    })
  }

  get flatOptions(): BaseOption[] {
    return [this.placementOption, ...this.lessonOptions]
  }

  getSavedOption(studentProgression: ProgressionRecord[]): FastPhonicsPeaksOption | BaseOption | undefined {
    const progressItem = studentProgression.find((p) => isMatch(p, this.payloadAttributes))

    if (progressItem?.showPlacementTest) {
      return this.placementOption
    } else if (progressItem?.lesson) {
      return this.lessonOptions.find((opt) => opt.lessonPosition === String(progressItem.lesson))
    }
  }
}

export class WritingLegendsLessonsProgressHandler implements ProgressHandler {
  intl: IntlService

  lessonInfo: LessonInfoService

  progressType = Precinct.WL_WRITING

  payloadAttributes

  static payloadAttributes = { product: 'writing_legends', precinct: 'writing' }

  constructor(intl: IntlService, lessonInfo: LessonInfoService) {
    this.intl = intl
    this.lessonInfo = lessonInfo

    this.payloadAttributes = WritingLegendsLessonsProgressHandler.payloadAttributes
  }

  @cached
  get lessonOptions() {
    return this.lessonInfo.lessonsFor(Precinct.WL_WRITING).map<WritingLegendsLessonsOption>((lesson) => {
      const { lesson_number: lessonNumber, map_number: mapNumber } = lesson
      assert(
        'unexpected lesson data when building progress options',
        typeof lessonNumber === 'number' && typeof mapNumber === 'number',
      )

      return new WritingLegendsLessonsOption(
        this.intl,
        this.progressType,
        { lessonNumber: String(lessonNumber), mapNumber: String(mapNumber) },
        this.payloadAttributes,
      )
    })
  }

  get placementOption(): BaseOption {
    return new PlacementOption(this.intl, this.progressType, this.payloadAttributes)
  }

  get flatOptions(): BaseOption[] {
    return [this.placementOption, ...this.lessonOptions]
  }

  getSavedOption(studentProgression: ProgressionRecord[]): WritingLegendsLessonsOption | BaseOption | undefined {
    const progressItem = studentProgression.find((p) => isMatch(p, this.payloadAttributes))

    if (!progressItem) return

    // 1. Placement override (student has placement test override if pendingPosition is 0)
    const isPlacementTest = progressItem.pendingPosition === 0 || progressItem.pendingPosition === '0'

    if (isPlacementTest) {
      return this.placementOption
    }

    // 2. Lesson override (student has lesson override if pendingPosition is not null and not 0)
    const isLessonOverride =
      progressItem.pendingPosition !== undefined &&
      progressItem.pendingPosition !== null &&
      progressItem.pendingPosition !== 0 &&
      progressItem.pendingPosition !== '0'

    if (isLessonOverride) {
      // If it has been set recently, it will be a pending progress
      return this.lessonOptions.find((opt) => opt.lessonPosition === String(progressItem.pendingPosition))
    }

    // 3. Normal progression
    if (progressItem.currentPosition) {
      return this.lessonOptions.find((opt) => opt.lessonPosition === String(progressItem.currentPosition))
    }
  }
}

export class MathseedsLessonsProgressHandler implements ProgressHandler {
  intl: IntlService

  lessonInfo: LessonInfoService

  gradeSets: GradeSetsService

  progressType = Precinct.MS_LESSONS

  payloadAttributes

  static payloadAttributes = { product: 'maths', precinct: 'my_lessons' }

  /**
   * Pass in the required services - can't inject ember services directly into native classes.
   */
  constructor(intl: IntlService, lessonInfo: LessonInfoService, gradeSets: GradeSetsService) {
    this.intl = intl
    this.lessonInfo = lessonInfo
    this.gradeSets = gradeSets

    this.payloadAttributes = MathseedsLessonsProgressHandler.payloadAttributes
  }

  get placementOption(): BaseOption {
    return new PlacementOption(this.intl, this.progressType, this.payloadAttributes)
  }

  @cached
  get lessonOptions() {
    return this.lessonInfo.lessonsFor(Precinct.MS_LESSONS).map<MathseedsLessonsOption>((lesson) => {
      const { lesson_number: lessonNumber, name, grade_position: gradePosition } = lesson

      assert(
        'unexpected lesson data when building progress options',
        typeof gradePosition === 'number' && typeof lessonNumber === 'string' && name,
      )

      return new MathseedsLessonsOption(
        this.intl,
        this.progressType,
        { lessonNumber, name, gradePosition },
        this.payloadAttributes,
      )
    })
  }

  get flatOptions(): BaseOption[] {
    return [this.placementOption]
  }

  get groupedOptions(): Record<string, MathseedsLessonsOption[]> {
    return groupBy(this.lessonOptions, (opt) => this.gradeSets.fullNameFromGradeSetByPosition(opt.gradePosition))
  }

  getSavedOption(studentProgression: ProgressionRecord[]): MathseedsLessonsOption | BaseOption | undefined {
    const progressItem = studentProgression.find((p) => isMatch(p, this.payloadAttributes))

    if (progressItem?.showPlacementTest || progressItem?.currentPosition === 'placement') {
      return this.placementOption
    } else if (progressItem?.currentPosition) {
      return this.lessonOptions.find((opt) => opt.lessonPosition === progressItem.currentPosition)
    }
  }
}

export class MathseedsMentalMinuteProgressHandler implements ProgressHandler {
  intl: IntlService

  lessonInfo: LessonInfoService

  gradeSets: GradeSetsService

  categories: MathseedsMentalMinuteCategory[]

  get progressType() {
    return `${Precinct.MS_MENTAL_MINUTE}-${this.categories.join('-')}`
  }

  payloadAttributes

  static payloadAttributes = { product: 'maths', precinct: 'mental_minute' }

  /**
   * Pass in the required services - can't inject ember services directly into native classes.
   */
  constructor(
    intl: IntlService,
    lessonInfo: LessonInfoService,
    gradeSets: GradeSetsService,
    categories: MathseedsMentalMinuteCategory[],
  ) {
    this.intl = intl
    this.lessonInfo = lessonInfo
    this.gradeSets = gradeSets
    this.categories = categories

    this.payloadAttributes = MathseedsMentalMinuteProgressHandler.payloadAttributes
  }

  @cached
  get lessonOptions() {
    return this.lessonInfo
      .lessonsFor(Precinct.MS_MENTAL_MINUTE)
      .map<MathseedsMentalMinuteOption>((lesson) => {
        const { lesson_number: lessonNumber, name, category, grade } = lesson

        assert(
          'unexpected lesson data when building progress options',
          typeof lessonNumber === 'number' && typeof grade === 'number' && category && name,
        )

        const lowercasedCategory = category.toLowerCase()
        assert('unknown category for mental minute lesson', isMathseedsMentalMinuteCategory(lowercasedCategory))

        return new MathseedsMentalMinuteOption(
          this.intl,
          this.progressType,
          { lessonNumber: String(lessonNumber), name, gradePosition: grade, category: lowercasedCategory },
          this.payloadAttributes,
        )
      })
      .filter((opt) => this.categories.includes(opt.category))
  }

  get groupedOptions(): Record<string, MathseedsMentalMinuteOption[]> {
    return groupBy(this.lessonOptions, (opt) => this.gradeSets.fullNameFromGradeSetByPosition(opt.gradePosition))
  }

  getSavedOption(studentProgression: ProgressionRecord[]): MathseedsMentalMinuteOption | BaseOption | undefined {
    const progressItem = studentProgression.find((p) => isMatch(p, this.payloadAttributes))

    let lessonSetResult = progressItem?.results?.lesson_set?.addition_subtraction
    if (this.categories.includes(MathseedsMentalMinuteCategory.Multiplication)) {
      lessonSetResult = progressItem?.results?.lesson_set?.multiplication_division
    }

    return this.lessonOptions.find((opt) => opt.lessonPosition === String(lessonSetResult))
  }
}

export class MathseedsPrimeLessonsProgressHandler implements ProgressHandler {
  intl: IntlService

  progressionSequence: ProgressionSequenceService

  curriculumId: string

  threadId: MathseedsPrimeThread

  // delete when tui_ms_prime_grade_three, tui_ms_prime_grade_five, tui_ms_prime_grade_six released
  disabledGradeNames: string[]

  get progressType() {
    return `${Precinct.MSP_LESSONS}-${this.threadId}`
  }

  payloadAttributes

  static payloadAttributes = { product: 'mathseeds_prime', precinct: 'lessons' }

  constructor(
    intl: IntlService,
    progressionSequence: ProgressionSequenceService,
    curriculumId: string,
    threadId: MathseedsPrimeThread,
    disabledGradeNames: string[],
  ) {
    this.intl = intl
    this.progressionSequence = progressionSequence
    this.curriculumId = curriculumId
    this.threadId = threadId
    this.disabledGradeNames = disabledGradeNames

    this.payloadAttributes = { ...MathseedsPrimeLessonsProgressHandler.payloadAttributes, curriculum: curriculumId }
  }

  @cached
  get lessonOptions() {
    assert('missing lesson data when building progress options', this.progressionSequence.mathseedsPrimeSequence)
    return Object.values(this.progressionSequence.mathseedsPrimeSequence).reduce<MathseedsPrimeLessonsOption[]>(
      (options, lessonData) => {
        const skipOption = this.disabledGradeNames.includes(lessonData.gradeName)
        if (
          !skipOption &&
          lessonData.curriculumId === this.curriculumId &&
          lessonData.threadId === this.threadId &&
          lessonData.positionInLesson === 1
        ) {
          return [
            ...options,
            new MathseedsPrimeLessonsOption(
              this.intl,
              this.progressType,
              {
                threadId: lessonData.threadId,
                unitId: lessonData.unitId,
                lessonId: lessonData.lessonId,
                lessonName: lessonData.lessonName,
                gradeName: lessonData.gradeName,
              },
              this.payloadAttributes,
            ),
          ]
        }
        return options
      },
      [],
    )
  }

  get groupedOptions(): Record<string, MathseedsPrimeLessonsOption[]> {
    return groupBy(this.lessonOptions, (opt) => opt.gradeName)
  }

  getSavedOption(studentProgression: ProgressionRecord[]): MathseedsPrimeLessonsOption | BaseOption | undefined {
    const progressItem = studentProgression.find(
      (p) => isMatch(p, this.payloadAttributes) && p.thread === this.threadId,
    )

    if (progressItem?.unit && progressItem?.lesson) {
      return this.lessonOptions.find((opt) => opt.unitId === progressItem.unit && opt.lessonId === progressItem.lesson)
    }
  }
}
